// i18n deferred: admin/blog/legal pages are intentionally excluded from Step 2 migration.
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/layout/Layout";
import { useBlogPosts } from "../hooks/useBlog";
import {
  blogMatchesSearchQuery,
  blogMatchesSelectedTag,
  BlogListingSection,
  FeaturedCard,
  FilterToolbar,
  getBlogPrimaryImage,
} from "../components/blog";
import { IconWrapper, Typography } from "@/components/ui/atoms";
import { LandingHeader } from "@/components/layout/LandingHeader";
import { AlertCircleIcon } from "lucide-react";
import { Button } from "@/components/ui/atoms/Button";

const ALL_TAG = "all";
const PAGE_SIZE = 6;

export function BlogPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isError } = useBlogPosts(currentPage, PAGE_SIZE);
  const [selectedTag, setSelectedTag] = useState<string>(ALL_TAG);
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedFeaturedIndex, setDisplayedFeaturedIndex] = useState(0);
  const [isFeaturedVisible, setIsFeaturedVisible] = useState(true);

  const tagsWithIds = useMemo(() => {
    const byId = new Map<string, string>();
    (data?.items || []).forEach((post) => {
      post.tags?.forEach((tag) => {
        const id = tag.name.toLowerCase();
        if (!byId.has(id)) byId.set(id, tag.name);
      });
    });
    return [{ id: ALL_TAG, label: "All" }, ...Array.from(byId.entries()).map(([id, label]) => ({ id, label }))];
  }, [data?.items]);

  const filteredPosts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return (data?.items || []).filter(
      (post) =>
        blogMatchesSelectedTag(post, selectedTag, ALL_TAG) &&
        blogMatchesSearchQuery(post, normalizedQuery),
    );
  }, [data?.items, searchQuery, selectedTag]);

  const featuredPosts = useMemo(
    () => (data?.items || []).slice(0, 5).filter((post) => getBlogPrimaryImage(post)),
    [data?.items],
  );
  const featuredPost = featuredPosts[displayedFeaturedIndex] ?? featuredPosts[0];
  const gridPosts = filteredPosts;
  const hasFiltersApplied = selectedTag !== ALL_TAG || searchQuery.trim().length > 0;
  const paginationTotal = hasFiltersApplied ? filteredPosts.length : (data?.total ?? 0);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTag, searchQuery]);

  useEffect(() => {
    setDisplayedFeaturedIndex(0);
    setIsFeaturedVisible(true);
  }, [featuredPosts.length]);

  useEffect(() => {
    if (featuredPosts.length <= 1) return;
    let fadeTimeoutId: number | undefined;
    const intervalId = window.setInterval(() => {
      setIsFeaturedVisible(false);
      fadeTimeoutId = window.setTimeout(() => {
        setDisplayedFeaturedIndex((current) => (current + 1) % featuredPosts.length);
        setIsFeaturedVisible(true);
      }, 250);
    }, 6000);

    return () => {
      window.clearInterval(intervalId);
      if (fadeTimeoutId) window.clearTimeout(fadeTimeoutId);
    };
  }, [featuredPosts]);

  return (
    <Layout hideHeader fullPage className="bg-white min-h-screen">
      <LandingHeader />
      <BlogListingSection
        featured={
          isError ? (
            <div className="flex min-h-[42vh] items-center justify-center">
              <div className="mx-auto max-w-lg text-center">
                <IconWrapper size="xl" color="error"><AlertCircleIcon /></IconWrapper>
                <Typography as="p" variant="h5" color="primary" className="mt-4">
                  Oops! Something went wrong.
                </Typography>
                <Typography as="p" variant="body-md" color="muted" className="mt-2">
                  We couldn't load the articles right now. Please try again.
                </Typography>
                <div className="mt-5">
                  <Button
                    type="outlined"
                    intent="secondary"
                    size="sm"
                    label="Try again"
                    onClick={() => window.location.reload()}
                  />
                </div>
              </div>
            </div>
          ) : featuredPost ? (
            <div className={`transition-opacity duration-300 ${isFeaturedVisible ? "opacity-100" : "opacity-0"}`}>
              <FeaturedCard post={featuredPost} />
            </div>
          ) : null
        }
        isLoading={isLoading}
        controls={
          !isError ? (
            <FilterToolbar
              tabs={tagsWithIds}
              activeTab={selectedTag}
              onTabChange={setSelectedTag}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              tabsLoading={isLoading}
            />
          ) : null
        }
        gridPosts={!isLoading && !isError ? gridPosts : null}
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={() => {
          setSelectedTag(ALL_TAG);
          setSearchQuery("");
        }}
        currentPage={data?.page ?? currentPage}
        totalPages={data?.pageCount ?? 1}
        pageSize={PAGE_SIZE}
        totalItems={paginationTotal}
        onPageChange={setCurrentPage}
      />
    </Layout>
  );
}

export default BlogPage;
